import type { ActiveFocusUnit } from './focusService';

export interface ExitSlipPrefill {
  roadmap: string;
  unit: string;
  classes: string[];
}

interface OptionLike {
  value: string;
}

interface ExitSlipLesson {
  id: string;
  targetGroup?: { id: string } | null;
}

export interface RoadmapPartition {
  withTickets: Set<string>;
  withUnassigned: Set<string>;
  withAssigned: Set<string>;
}

export const partitionRoadmaps = (
  lessons: ReadonlyArray<ExitSlipLesson | null | undefined> | null | undefined,
  assignedLessonIds: ReadonlySet<string>,
): RoadmapPartition => {
  const partition: RoadmapPartition = {
    withTickets: new Set<string>(),
    withUnassigned: new Set<string>(),
    withAssigned: new Set<string>(),
  };

  for (const lesson of lessons ?? []) {
    const roadmapId = lesson?.targetGroup?.id;
    if (!roadmapId || !lesson?.id) continue;
    partition.withTickets.add(roadmapId);
    if (assignedLessonIds.has(lesson.id)) {
      partition.withAssigned.add(roadmapId);
    } else {
      partition.withUnassigned.add(roadmapId);
    }
  }

  return partition;
};

export const assignedLessonIdsFrom = (
  slips: ReadonlyArray<{ curriculumLesson?: { id: string } | null } | null | undefined> | null | undefined,
): Set<string> => {
  const ids = new Set<string>();
  for (const slip of slips ?? []) {
    if (slip?.curriculumLesson?.id) ids.add(slip.curriculumLesson.id);
  }
  return ids;
};

export const narrowRoadmapOptions = <T extends OptionLike>(options: T[], allowed: ReadonlySet<string>): T[] => {
  if (!allowed.size) return options;
  const narrowed = options.filter(option => allowed.has(option.value));
  return narrowed.length ? narrowed : options;
};

interface TargetLike {
  id: string;
  targetGroup?: { id: string } | null;
}

export const activeFocusRoadmaps = (
  focuses: readonly { isActive: boolean; targetId: string }[],
  targets: ReadonlyArray<TargetLike | null | undefined>,
  focusClassIds: Record<string, string[]>,
  classId: string,
): ActiveFocusUnit[] =>
  targets.flatMap(target =>
    target?.targetGroup?.id &&
    focusClassIds[target.id]?.includes(classId) &&
    focuses.some(focus => focus.isActive && focus.targetId === target.id)
      ? [{ targetId: target.id, roadmapId: target.targetGroup.id }]
      : [],
  );

interface PrefillArgs {
  dashboardClassIds: readonly string[];
  classOptions: readonly OptionLike[];
  roadmapOptions: readonly OptionLike[];
  preferredRoadmapId?: string;
  fallbackRoadmapIds?: ReadonlySet<string>;
}

export const resolveExitSlipPrefill = ({
  dashboardClassIds,
  classOptions,
  roadmapOptions,
  preferredRoadmapId = '',
  fallbackRoadmapIds = new Set<string>(),
}: PrefillArgs): ExitSlipPrefill => {
  const offered = new Set(classOptions.map(option => option.value));
  const roadmap = roadmapOptions.some(option => option.value === preferredRoadmapId)
    ? preferredRoadmapId
    : roadmapOptions.find(option => fallbackRoadmapIds.has(option.value))?.value ??
      (roadmapOptions.length === 1 ? roadmapOptions[0].value : '');

  return {
    roadmap,
    //TEAMB-3482: no unit prefill — Unit is optional and Manage lists every unit in the roadmap
    unit: '',
    classes: dashboardClassIds.filter(id => offered.has(id)).slice(0, 1),
  };
};
